import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { X } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/ui/button'
import { ToastContext, type ToastInput, type ToastKind } from '@/shared/components/toastContext'
import { cn } from '@/shared/lib/utils'

interface ToastItem extends ToastInput {
  id: string
  kind: ToastKind
}

const AUTO_DISMISS_MS: Record<ToastKind, number> = { info: 5000, success: 5000, error: 9000 }

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const dismissToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id)
    if (timer !== undefined) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
    setToasts((items) => items.filter((item) => item.id !== id))
  }, [])

  const toastsRef = useRef<ToastItem[]>([])
  useEffect(() => {
    toastsRef.current = toasts
  }, [toasts])

  const showToast = useCallback(
    (toast: ToastInput) => {
      const kind = toast.kind ?? 'info'
      const duplicate = toast.id !== undefined
        ? toastsRef.current.find((item) => item.id === toast.id)
        : toastsRef.current.find(
            (item) => item.kind === kind && item.title === toast.title && item.description === toast.description,
          )
      if (duplicate !== undefined) {
        if (toast.id !== undefined) {
          setToasts((items) => items.map((item) => item.id === duplicate.id
            ? { ...item, ...toast, id: duplicate.id, kind }
            : item))
        }
        const timer = timersRef.current.get(duplicate.id)
        if (timer !== undefined) {
          clearTimeout(timer)
          timersRef.current.set(duplicate.id, setTimeout(() => dismissToast(duplicate.id), AUTO_DISMISS_MS[kind]))
        }
        return
      }
      const id = toast.id ?? globalThis.crypto?.randomUUID?.() ?? String(Date.now())
      setToasts((items) => [
        ...items.slice(-2),
        {
          id,
          kind,
          title: toast.title,
          description: toast.description,
          actionLabel: toast.actionLabel,
          onAction: toast.onAction,
        },
      ])
      if (!toast.actionLabel || !toast.onAction) {
        const timer = setTimeout(() => dismissToast(id), AUTO_DISMISS_MS[kind])
        timersRef.current.set(id, timer)
      }
    },
    [dismissToast],
  )

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach((timer) => clearTimeout(timer))
      timers.clear()
    }
  }, [])

  const value = useMemo(() => ({ showToast, dismissToast }), [dismissToast, showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-[calc(var(--brand-bottom-nav-height)+0.75rem+env(safe-area-inset-bottom))] right-4 z-[var(--brand-z-toast)] flex w-[calc(100%-2rem)] max-w-sm flex-col gap-3 lg:bottom-6">
        {toasts.map((toast) => (
          <section
            key={toast.id}
            role={toast.kind === 'error' ? 'alert' : 'status'}
            className={cn(
              'pointer-events-auto rounded-[var(--brand-radius-lg)] border border-[var(--brand-border-soft)] bg-[var(--brand-surface)] p-4 text-card-foreground shadow-[var(--brand-shadow-2)]',
              toast.kind === 'success' && 'border-[var(--brand-success)]/50',
              toast.kind === 'error' && 'border-[var(--brand-error)]/50',
              toast.kind === 'info' && 'border-[var(--brand-info)]/45',
            )}
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[var(--brand-text-primary)]">{toast.title}</p>
                {toast.description ? <p className="mt-1 text-sm text-[var(--brand-text-secondary)]">{toast.description}</p> : null}
                {toast.actionLabel && toast.onAction ? (
                  <Button
                    type="button"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      toast.onAction?.()
                      dismissToast(toast.id)
                    }}
                  >
                    {toast.actionLabel}
                  </Button>
                ) : null}
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => dismissToast(toast.id)}>
                <X className="size-4" aria-hidden />
                <span className="sr-only">{t('ui.toast.close')}</span>
              </Button>
            </div>
          </section>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
